import { getOrganizationNamesWithUpper } from '@/features/analysis/_components/analysis-visualizer/AnalysisVisualizer';
import type { AnalysisPageSearchQueryStates } from '@/features/analysis/_hooks/search-query-states';
import type { FundSheetAnalysisPageSearchQueryStates } from '@/features/analysis/fund-sheet/_hooks/search-query-states';
import { useNestedExpenditureBudgetPurposeOptions } from '@/features/master-table/hooks';
import { usePoliciesOptions } from '@/features/master-table/policy/_hooks/policy';
import type { ProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import { useQueryStore } from '@/features/search/_hooks/query-store';
import { useProjectsSearchStore } from '@/features/search/_stores/projects-search-store';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/organization/_hooks/organization';
import { getSelectedCascaderIds, isPS } from '@/utils';
import { type FC, useEffect, useMemo, useState } from 'react';
import type { ProjectsSearchAccordionProviderType } from '../_utils/projects-search';

export const ProjectsSearchAccordionInitializeStore: FC<{
  type: ProjectsSearchAccordionProviderType;
  searchQueryStates:
    | ProjectsPageSearchQueryStates
    | AnalysisPageSearchQueryStates
    | FundSheetAnalysisPageSearchQueryStates;
  isDebouncing: boolean;
}> = ({ type, searchQueryStates, isDebouncing }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  const {
    setData,
    setCascaderTargetIds,
    type: currentStoreType,
    setIsLoading,
    reset,
  } = useProjectsSearchStore();

  const organizationsOptions = useOrganizationOptionsRecursive();

  const policiesOptions = usePoliciesOptions();
  const expenditureBudgetPurposesOptions =
    useNestedExpenditureBudgetPurposeOptions({
      params: { type: 'revenueNature' },
    });

  const selectedOrganizationIdsForPs = useMemo(
    () =>
      searchQueryStates.organizationIds?.map((v) => {
        return getOrganizationNamesWithUpper({
          id: v,
          organizations: organizationsOptions.data ?? [],
        }).ids.map((v) => v.toString());
      }),
    [searchQueryStates.organizationIds, organizationsOptions.data],
  );

  const selectedOtherResponsibleOrganizationIds = useMemo(() => {
    const otherResponsibleOrganizationIdsParamForPs =
      searchQueryStates.otherResponsibleOrganizationIds;
    if (
      otherResponsibleOrganizationIdsParamForPs &&
      organizationsOptions.data
    ) {
      return getSelectedCascaderIds(
        organizationsOptions.data,
        otherResponsibleOrganizationIdsParamForPs,
      );
    }
    return [];
  }, [
    organizationsOptions.data,
    searchQueryStates.otherResponsibleOrganizationIds,
  ]);

  const selectedPolicyIdsForPs = useMemo(() => {
    const policyIdsParamForPs = searchQueryStates.policyIds;
    if (policyIdsParamForPs && policiesOptions.data) {
      return getSelectedCascaderIds(policiesOptions.data, policyIdsParamForPs);
    }
    return [];
  }, [policiesOptions.data, searchQueryStates.policyIds]);

  const selectedExpenditureBudgetPurposeIdsForPs = useMemo(() => {
    const expenditureBudgetPurposeIdsParamForPs =
      searchQueryStates.expenditureBudgetPurposeIds;
    if (
      expenditureBudgetPurposeIdsParamForPs &&
      expenditureBudgetPurposesOptions.data
    ) {
      return getSelectedCascaderIds(
        expenditureBudgetPurposesOptions.data,
        expenditureBudgetPurposeIdsParamForPs,
      );
    }
    return [];
  }, [
    expenditureBudgetPurposesOptions.data,
    searchQueryStates.expenditureBudgetPurposeIds,
  ]);

  const queryStore = useQueryStore(searchQueryStates.cascader);
  const query = queryStore.data?.query;

  // NOTE: 詳細画面からのブラウザバックや、事業<=>分析画面の切り替えのために初期化を行う
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      reset(type);
    }
  }, [type, reset, isInitialized]);

  // NOTE: ストアの更新を1つのuseEffectにまとめる
  useEffect(() => {
    // オプションデータの更新
    setData({
      organizationsOptions: organizationsOptions.data,
      policiesOptions: policiesOptions.data,
      expenditureBudgetPurposesOptions: expenditureBudgetPurposesOptions.data,
    });

    // カスケーダーターゲットIDの更新
    setCascaderTargetIds(
      isPS()
        ? {
            expenditureBudgetPurposeIds:
              selectedExpenditureBudgetPurposeIdsForPs,
            organizationIds: selectedOrganizationIdsForPs ?? null,
            otherResponsibleOrganizationIds:
              selectedOtherResponsibleOrganizationIds,
            policyIds: selectedPolicyIdsForPs,
          }
        : {
            expenditureBudgetPurposeIds:
              query?.expenditureBudgetPurposeIds ?? null,
            organizationIds: query?.organizationIds ?? null,
            otherResponsibleOrganizationIds:
              query?.otherResponsibleOrganizationIds ?? null,
            policyIds: query?.policyIds ?? null,
          },
    );
  }, [
    // オプションデータの依存
    organizationsOptions.data,
    policiesOptions.data,
    expenditureBudgetPurposesOptions.data,
    // カスケーダーIDの依存
    selectedExpenditureBudgetPurposeIdsForPs,
    selectedOrganizationIdsForPs,
    selectedOtherResponsibleOrganizationIds,
    selectedPolicyIdsForPs,
    query,
    // アクション関数
    setData,
    setCascaderTargetIds,
  ]);

  // 初期化前、またはtypeが異なる場合のみローディング
  const isTypeChanging =
    !isInitialized ||
    (currentStoreType !== undefined && type !== currentStoreType);

  // データ取得中の判定
  const isDataLoading =
    policiesOptions.isPending ||
    organizationsOptions.isPending ||
    expenditureBudgetPurposesOptions.isPending ||
    (!isPS() &&
      searchQueryStates.cascader &&
      (!queryStore.data || queryStore.isPending || queryStore.isFetching));

  // ページ遷移時は初期データがある場合はローディングを表示しない
  const hasInitialData =
    !!organizationsOptions.data?.length &&
    !!policiesOptions.data?.length &&
    !!expenditureBudgetPurposesOptions.data?.length;

  const isLoading =
    isTypeChanging || !hasInitialData || isDataLoading || isDebouncing;

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  return null;
};
